import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { blogPosts, blogCategories } from "@/mocks/blog";
import { useScrollAnimation, useStaggerAnimation } from "@/hooks/useScrollAnimation";
import { Link } from "react-router-dom";
import PageSEO from "@/components/base/PageSEO";

export default function Blog() {
  const [activeCategory, setActiveCategory] = useState("全部文章");
  const headerAnim = useScrollAnimation();
  const cardsAnim = useStaggerAnimation(blogPosts.length, { threshold: 0.05 });

  const filtered = activeCategory === "全部文章"
    ? blogPosts
    : blogPosts.filter(p => p.category === activeCategory);

  const featured = blogPosts.filter(p => p.featured);

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      <PageSEO
        title="穿搭專欄｜台中熟齡女性顯瘦穿搭技巧 造型指南"
        description="台中設計師女裝Outlet穿搭專欄，由專業造型顧問分享熟齡穿搭技巧、顯瘦方法與質感日常造型，幫助您找到最適合的風格。"
        keywords="熟齡穿搭技巧,台中女性造型,顯瘦穿搭指南,設計師品牌搭配"
        canonical="https://www.example.com/blog"
      />
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative h-[45vh] min-h-[340px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=elegant%20taiwanese%20mature%20women%20fashion%20editorial%20blog%20content%20reading%20style%20guide%20warm%20cream%20beige%20background%20designer%20brand%20quality%20sophisticated%20atmosphere&width=1400&height=650&seq=bloghero&orientation=landscape"
            alt="穿搭專欄"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/55" />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <p className="text-[#f5d8b8] text-[1rem] tracking-[0.3em] uppercase font-medium mb-3">Style Column</p>
            <h1 className="text-white text-[2.75rem] md:text-[3.25rem] font-bold leading-[1.3] mb-3">穿搭專欄</h1>
            <p className="text-white/80 text-[1.1875rem] max-w-lg leading-[1.75]">由專業造型顧問撰寫，分享熟齡穿搭、顯瘦技巧與質感日常，讓穿衣成為一件愉快的事。</p>
          </div>
        </section>

        {/* Featured Posts */}
        <section
          ref={headerAnim.ref as React.RefObject<HTMLElement>}
          className={`py-16 md:py-24 bg-[#faf8f5] transition-all duration-700 ${headerAnim.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <div className="w-full px-6 md:px-16 max-w-[1400px] mx-auto">
            <div className="mb-10">
              <p className="text-[#8B5E52] text-[1rem] tracking-[0.25em] uppercase font-medium mb-2">Featured</p>
              <h2 className="text-[#3d2b1f] text-[2.125rem] font-bold">編輯精選</h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {featured.map(post => (
                <Link
                  key={post.id}
                  to={`/blog/${post.id}`}
                  className="group bg-white rounded-lg overflow-hidden cursor-pointer block"
                >
                  <div className="relative h-[280px] overflow-hidden">
                    <img src={post.image} alt={post.title} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                    <div className="absolute top-4 left-4 flex gap-2 flex-wrap">
                      {post.tags.slice(0, 2).map(tag => (
                        <span key={tag} className="px-3 py-1 bg-[#3d2b1f]/80 text-white rounded-full text-[0.875rem] font-medium backdrop-blur-sm">{tag}</span>
                      ))}
                    </div>
                  </div>
                  <div className="p-7">
                    <p className="text-[#8B5E52] text-[1rem] font-medium mb-2">{post.category}</p>
                    <h3 className="text-[#3d2b1f] text-[1.375rem] font-bold leading-[1.45] mb-3 group-hover:text-[#8B5E52] transition-colors">{post.title}</h3>
                    <p className="text-[#7a6455] text-[1.0625rem] leading-[1.75] mb-5 line-clamp-2">{post.excerpt}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-[#a09080] text-[1rem]">{post.date} · {post.readTime}</span>
                      <span className="text-[#8B5E52] text-[1rem] font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                        閱讀全文 <i className="ri-arrow-right-line"></i>
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* All Posts */}
        <section className="py-8 pb-24 bg-[#faf8f5]">
          <div className="w-full px-6 md:px-16 max-w-[1400px] mx-auto">
            {/* Category Filter */}
            <div className="flex flex-wrap gap-3 mb-6">
              {blogCategories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-5 py-2 rounded-full text-[1rem] font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${activeCategory === cat ? "bg-[#3d2b1f] text-[#faf8f5]" : "bg-[#f5ede4] text-[#7a6455] hover:bg-[#e8d8c8]"}`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Count Label */}
            <div className="mb-8">
              <p className="text-[#7a6455] text-[0.9375rem]">
                {activeCategory === "全部文章"
                  ? <><span className="text-[#3d2b1f] font-bold">{filtered.length}</span> 篇文章</>
                  : <>共 <span className="text-[#8B5E52] font-bold">{filtered.length}</span> 篇 <span className="font-semibold text-[#3d2b1f]">{activeCategory}</span> 文章</>
                }
              </p>
            </div>

            <div
              ref={cardsAnim.ref as React.RefObject<HTMLDivElement>}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {filtered.map((post, i) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.id}`}
                  className={`group bg-white rounded-lg overflow-hidden cursor-pointer block transition-all duration-700 ${cardsAnim.visibleItems[i] !== false ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
                >
                  <div className="relative h-[220px] overflow-hidden">
                    <img src={post.image} alt={post.title} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                    <span className="absolute top-3 left-3 px-3 py-1 bg-[#8B5E52] text-white rounded-full text-[0.875rem] font-medium">{post.category}</span>
                  </div>
                  <div className="p-6">
                    <h3 className="text-[#3d2b1f] text-[1.125rem] font-bold leading-[1.45] mb-3 line-clamp-2 group-hover:text-[#8B5E52] transition-colors">{post.title}</h3>
                    <p className="text-[#7a6455] text-[1.0625rem] leading-[1.75] mb-4 line-clamp-2">{post.excerpt}</p>
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {post.tags.map(tag => (
                        <span key={tag} className="px-2.5 py-1 bg-[#f5ede4] text-[#8B5E52] rounded-full text-[0.8125rem] font-medium">{tag}</span>
                      ))}
                    </div>
                    <div className="flex items-center justify-between pt-3 border-t border-[#ede8e0]">
                      <span className="text-[#a09080] text-[0.9375rem]">{post.date}</span>
                      <span className="text-[#8B5E52] text-[0.9375rem] font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                        {post.readTime} <i className="ri-arrow-right-line"></i>
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
